import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer } from "@/components/admin/ui";
import { CustomerPickerField } from "./CustomerPickerField";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";

export const NewTicketModal = ({ API, meta, initialCustomer, onClose, onCreated }) => {
  const [customer, setCustomer] = useState(initialCustomer || null);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(meta.categories[0] || "General Inquiry");
  const [priority, setPriority] = useState("medium");
  const [teamId, setTeamId] = useState("");
  const [userId, setUserId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!customer || !subject.trim()) return;
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/org/support/tickets`, {
        customer_id: customer.customer_id, subject: subject.trim(), description: description.trim(),
        category, priority, assigned_team_id: teamId || null, assigned_user_id: userId || null,
      }, { withCredentials: true });
      onCreated();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <DetailDrawer open onClose={onClose} title="New Ticket" subtitle="Create a Support Ticket for a customer." testId="new-ticket-drawer">
      <div className="space-y-4" data-testid="new-ticket-form">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="new-ticket-error">{error}</p>}
        <div>
          <label className={labelClass}>Customer</label>
          <div className="mt-1"><CustomerPickerField API={API} value={customer} onChange={setCustomer} testId="new-ticket-customer-picker" /></div>
        </div>
        <div><label className={labelClass}>Subject</label><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="new-ticket-subject" className={inputClass} /></div>
        <div><label className={labelClass}>Description</label><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-ticket-description" className={inputClass} /></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="new-ticket-category" className={inputClass}>
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="new-ticket-priority" className={inputClass}>
              {meta.priorities.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Team (optional)</label>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="new-ticket-team" className={inputClass}>
              <option value="">Unassigned queue</option>
              {meta.teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Assignee (optional)</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="new-ticket-assignee" className={inputClass}>
              <option value="">Unassigned</option>
              {meta.users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="new-ticket-cancel" className={outlineBtn}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving || !customer || !subject.trim()} data-testid="new-ticket-submit" className={btn}>Create Ticket</button>
        </div>
      </div>
    </DetailDrawer>
  );
};
